import { useCallback, useEffect, useMemo, useState } from 'react';
import { createHookContext } from '@clover-util/react-context';
import { useTranslation } from 'react-i18next';
import { DateTime, Interval } from 'luxon';
import { WeekDay } from 'olo-service/models';
import { canScheduleOrderToday, getDateLabel } from './ScheduledOrderMethods.util';
import { useScheduledOrderMethods } from './ScheduledOrderMethods';
import { useMerchant } from './merchant';
import { toDateTime } from './merchant/util';
import { useDelivery } from './Delivery';
import { formatTimeForDelivery } from './Delivery.util';
import { useServiceType } from './serviceType';

export const { useScheduledOrderDisplay, ScheduledOrderDisplayProvider } = createHookContext('ScheduledOrderDisplay', () => {
  const { t } = useTranslation();
  const { merchant, service } = useMerchant();
  const { scheduledOrder, updateScheduledOrder, clearScheduledOrder } = useScheduledOrderMethods();
  const { isDeliveryChosen } = useServiceType();
  const { initialEstimations } = useDelivery();
  const maxDays = merchant?.scheduledOrdering?.maxDays;
  const timezone = merchant?.timezone || 'local';
  const leadTime = useMemo(() => {
    if (isDeliveryChosen && initialEstimations?.deliveryTime) return formatTimeForDelivery(initialEstimations?.deliveryTime, merchant?.leadTime, timezone) as number;

    return merchant?.leadTime;
  }, [initialEstimations?.deliveryTime, isDeliveryChosen, merchant?.leadTime, timezone]);

  const now = useMemo(() => DateTime.local({ locale: 'en-US' }).setZone(timezone), [timezone]);

  const currentDate = useMemo(() => {
    if (!scheduledOrder.currentDate) return undefined;

    const currentDateTimeObj = DateTime.fromMillis(scheduledOrder.currentDate).setZone(timezone);

    return {
      label: t(`${getDateLabel(currentDateTimeObj)}`, { date: DateTime.fromMillis(scheduledOrder.currentDate).setZone(timezone) }),
      dateTime: scheduledOrder.currentDate
    };
  }, [scheduledOrder.currentDate, timezone, t]);

  const currentTime = useMemo(() => {
    if (!scheduledOrder.currentTime) return undefined;

    return {
      label: t('scheduledOrder.time', { time: DateTime.fromMillis(scheduledOrder.currentTime).setZone(timezone) }),
      dateTime: scheduledOrder.currentTime
    };
  }, [scheduledOrder.currentTime, t, timezone]);

  const [displayValueTime, setDisplayValueTime] = useState(currentTime);
  const [displayValueDate, setDisplayValueDate] = useState(currentDate);

  const dates = useMemo(() => {
    if (!service) return [];

    return Interval.after(now, { days: maxDays }).splitBy({ days: 1 })
    .filter(interval => {
      const daysHours = service.hours[interval.start.weekdayLong.toLowerCase() as WeekDay];

      if (interval.start.toRelativeCalendar() === 'today') return canScheduleOrderToday(daysHours, now, timezone, leadTime);

      return daysHours?.length !== 0;
    })
    .map(({ start }) => {
      return {
        label: t(`${getDateLabel(start)}`, { date: start }),
        dateTime: start.valueOf()
      };
    });

  }, [service, now, maxDays, timezone, leadTime, t]);

  const times = useMemo(() => {
    if (!service || dates.length < 1) return [];

    const chosenDate = displayValueDate?.dateTime ? DateTime.fromMillis(displayValueDate?.dateTime,{ locale: 'en-US' }).setZone(timezone) : DateTime.fromMillis(dates[0].dateTime, { locale: 'en-US' }).setZone(timezone);
    const isToday = chosenDate.toRelativeCalendar() === 'today';

    const daysHours = service.hours[chosenDate.weekdayLong.toLowerCase() as WeekDay];

    if (!daysHours) return [];

    return daysHours.flatMap(hours => {
      const startTime = toDateTime(hours.start, timezone);
      const endTime = toDateTime(hours.end, timezone);

      const datesArray = Interval.fromDateTimes(startTime, endTime).splitBy({ minutes: 15 })
        .filter(interval => interval.isAfter(isToday && now > startTime ? now.plus({ minutes: leadTime }) : startTime.plus({ minutes: leadTime })))
        .map(({ start }) => ({
          label: t('scheduledOrder.time', { time: start }),
          dateTime: start.valueOf()
        }));

      return datesArray.length === 0 ? datesArray : datesArray.concat({
        label: t('scheduledOrder.time', { time: endTime }),
        dateTime: endTime.valueOf()
      });
    });

  }, [service, dates, displayValueDate?.dateTime, timezone, t, now, leadTime]);

  const [firstDate] = dates;
  const [firstTime] = times;

  // On intilization make sure to update with shown time
  useEffect(() => {
    if (!displayValueDate && !displayValueTime) return;

    if (displayValueDate && !displayValueTime) {
      setDisplayValueTime(firstTime);
    }

  }, [displayValueDate, displayValueTime, firstDate, firstTime, updateScheduledOrder]);

  return {
    dates,
    times,
    currentDate,
    currentTime,
    displayValueTime,
    displayValueDate,
    updateDisplayValueTime: useCallback((value) => {
      !displayValueDate && setDisplayValueDate(firstDate);
      setDisplayValueTime(value);
    }, [displayValueDate, firstDate]),
    updateDisplayValueDate: useCallback((value) => {
      setDisplayValueDate(value);
      setDisplayValueTime(undefined);
    }, []),
    noAvailableTimes: useMemo(() => dates.length === 0 || times.length === 0, [dates.length, times.length]),
    resetScheduledOrderState: useCallback(() => {
      setDisplayValueDate(undefined);
      setDisplayValueTime(undefined);
      clearScheduledOrder();
    }, [clearScheduledOrder]),
    resetDisplayValueState: useCallback(() => {
      setDisplayValueDate(currentDate || firstDate);
      setDisplayValueTime(currentTime || firstTime);
    }, [currentDate, currentTime, firstDate, firstTime]),
    resetScheduledOrderToInitialValues: useCallback(() => {
      updateScheduledOrder({ currentDate: firstDate?.dateTime, currentTime: firstTime?.dateTime });
      setDisplayValueDate(firstDate);
      setDisplayValueTime(firstTime);
    }, [firstDate, firstTime, updateScheduledOrder]),
    resetToFirstAvailableTime: useCallback(() => {
      setDisplayValueDate(undefined);
      setDisplayValueTime(undefined);
    }, [])
  };
});
